import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Rating,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const TeacherEvaluation = () => {
  const [evaluationData, setEvaluationData] = useState({
    teacherId: '',
    date: null,
    subject: '',
    class: '',
    lessonTopic: '',
    evaluator: ''
  });

  const [ratings, setRatings] = useState({
    lessonPlanning: 0,
    contentKnowledge: 0,
    teachingMethods: 0,
    classManagement: 0,
    studentEngagement: 0,
    timeManagement: 0,
    assessmentMethods: 0,
    professionalism: 0
  });

  const [comments, setComments] = useState({
    strengths: '',
    areasForImprovement: '',
    recommendations: ''
  });

  // Sample data for previous evaluations
  const previousEvaluations = [
    {
      id: 1,
      date: '2024-01-15',
      teacher: 'John Doe',
      subject: 'Mathematics',
      averageRating: 4.5,
      evaluator: 'Head Teacher'
    },
    {
      id: 2,
      date: '2024-01-18',
      teacher: 'Jane Smith',
      subject: 'English',
      averageRating: 4.2,
      evaluator: 'Deputy Head'
    }
  ];

  const teachers = [
    { id: 1, name: 'John Doe', subject: 'Mathematics' },
    { id: 2, name: 'Jane Smith', subject: 'English' },
    { id: 3, name: 'Robert Johnson', subject: 'Science' }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    // Calculate average rating
    const ratingValues = Object.values(ratings);
    const averageRating = ratingValues.reduce((a, b) => a + b) / ratingValues.length;
    
    console.log({
      evaluationData,
      ratings,
      comments,
      averageRating
    });
    // Here you would typically save to your backend
  };

  const ratingCategories = [
    { name: 'lessonPlanning', label: 'Lesson Planning & Preparation' },
    { name: 'contentKnowledge', label: 'Subject Content Knowledge' },
    { name: 'teachingMethods', label: 'Teaching Methods & Strategies' },
    { name: 'classManagement', label: 'Classroom Management' },
    { name: 'studentEngagement', label: 'Student Engagement' },
    { name: 'timeManagement', label: 'Time Management' },
    { name: 'assessmentMethods', label: 'Assessment & Feedback' },
    { name: 'professionalism', label: 'Professional Conduct' }
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Teacher Evaluation Form
        </Typography>

        <Paper sx={{ p: 3, mb: 4 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Select Teacher</InputLabel>
                  <Select
                    value={evaluationData.teacherId}
                    onChange={(e) => setEvaluationData({...evaluationData, teacherId: e.target.value})}
                    label="Select Teacher"
                  >
                    {teachers.map((teacher) => (
                      <MenuItem key={teacher.id} value={teacher.id}>
                        {teacher.name} - {teacher.subject}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Evaluation Date"
                  value={evaluationData.date}
                  onChange={(newValue) => setEvaluationData({...evaluationData, date: newValue})}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Class/Grade"
                  value={evaluationData.class}
                  onChange={(e) => setEvaluationData({...evaluationData, class: e.target.value})}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Lesson Topic"
                  value={evaluationData.lessonTopic}
                  onChange={(e) => setEvaluationData({...evaluationData, lessonTopic: e.target.value})}
                />
              </Grid>

              {/* Performance Ratings */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Performance Ratings
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {ratingCategories.map((category) => (
                  <Box key={category.name} sx={{ mb: 2 }}>
                    <Typography component="legend">{category.label}</Typography>
                    <Rating
                      name={category.name}
                      value={ratings[category.name]}
                      onChange={(event, newValue) => {
                        setRatings({...ratings, [category.name]: newValue});
                      }}
                      precision={0.5}
                    />
                  </Box>
                ))}
              </Grid>

              {/* Comments Section */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Evaluation Comments
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Strengths"
                  value={comments.strengths}
                  onChange={(e) => setComments({...comments, strengths: e.target.value})}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Areas for Improvement"
                  value={comments.areasForImprovement}
                  onChange={(e) => setComments({...comments, areasForImprovement: e.target.value})}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Recommendations"
                  value={comments.recommendations}
                  onChange={(e) => setComments({...comments, recommendations: e.target.value})}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{ mt: 2 }}
                >
                  Submit Evaluation
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>

        {/* Previous Evaluations Table */}
        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Previous Evaluations
        </Typography>
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Teacher</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Average Rating</TableCell>
                <TableCell>Evaluator</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {previousEvaluations.map((evaluation) => (
                <TableRow key={evaluation.id}>
                  <TableCell>{evaluation.date}</TableCell>
                  <TableCell>{evaluation.teacher}</TableCell>
                  <TableCell>{evaluation.subject}</TableCell>
                  <TableCell>
                    <Rating value={evaluation.averageRating} precision={0.1} readOnly />
                  </TableCell>
                  <TableCell>{evaluation.evaluator}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Container>
    </LocalizationProvider>
  );
};

export default TeacherEvaluation;
